import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

const FEEDBACK_OPTIONS = [
  "Too easy",
  "Too difficult",
  "Confusing order",
  "Needs more examples",
  "Too long",
  "Not what I expected",
] as const;

interface CourseFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
}

export const CourseFeedbackDialog: React.FC<CourseFeedbackDialogProps> = ({
  open,
  onOpenChange,
  courseId,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [categories, setCategories] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [allowAiAdjust, setAllowAiAdjust] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = categories.length > 0 || message.trim().length > 0;

  const toggleCategory = (cat: string) => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const resetForm = () => {
    setCategories([]);
    setMessage("");
    setAllowAiAdjust(true);
  };

  const handleSubmit = async () => {
    if (!user || !canSubmit) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.from("course_feedback" as any).insert({
        user_id: user.id,
        course_id: courseId,
        categories,
        message: message.trim() || null,
        allow_ai_adjust: allowAiAdjust,
      } as any);
      if (error) throw error;
      toast({ title: "Thanks! Your feedback helps improve this course." });
      resetForm();
      onOpenChange(false);
    } catch {
      toast({ title: "Something went wrong", description: "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle>Help us improve this course</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Your feedback helps refine the AI and improve future lessons.
          </p>
        </DialogHeader>

        {/* Quick feedback chips */}
        <div className="space-y-3">
          <p className="text-sm font-medium">How did this course feel?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {FEEDBACK_OPTIONS.map((option) => {
              const selected = categories.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleCategory(option)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors
                    ${
                      selected
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border bg-background text-muted-foreground hover:bg-muted"
                    }`}
                >
                  {option}
                </button>
              );
            })}
          </div>
        </div>

        {/* Optional textarea */}
        <div className="space-y-2">
          <p className="text-sm font-medium">Tell us more (optional)</p>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What could be improved?"
            rows={3}
          />
        </div>

        {/* AI checkbox */}
        <div className="flex items-start gap-3">
          <Checkbox
            checked={allowAiAdjust}
            onCheckedChange={(v) => setAllowAiAdjust(v === true)}
            id="ai-adjust"
            className="mt-0.5"
          />
          <div className="space-y-0.5">
            <label htmlFor="ai-adjust" className="text-sm font-medium cursor-pointer">
              Allow AI to adjust this course based on my feedback
            </label>
            <p className="text-xs text-muted-foreground">
              This may reorganize lessons or add clarifications.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
